import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area, ComposedChart, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from "recharts";
import { TrendUp, TrendDown, Users, Clock, WarningCircle, ArrowRight, CurrencyCircleDollar, Calendar, Receipt } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

const fmt = (n) => `₺${(n || 0).toLocaleString("tr-TR", { maximumFractionDigits: 0 })}`;

const DashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [overdue, setOverdue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterMode, setFilterMode] = useState("all");
  const [month, setMonth] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPersonel = user?.role === "personel";

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterMode === "month" && month) params.append("month", month);
      if (filterMode === "range") {
        if (startDate) params.append("start_date", startDate);
        if (endDate) params.append("end_date", endDate);
      }
      const [s, o] = await Promise.all([
        axios.get(`${API_URL}/api/dashboard/stats?${params}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/dashboard/overdue`, { withCredentials: true }),
      ]);
      setStats(s.data);
      setOverdue(o.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [API_URL, filterMode, month, startDate, endDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading || !stats) {
    return (
      <div className="flex items-center justify-center min-h-screen" data-testid="dashboard-loading">
        <div className="inline-block h-10 w-10 animate-spin rounded-full border-2 border-[hsl(var(--border))] border-t-[hsl(var(--text-strong))]"></div>
      </div>
    );
  }

  const MONOCHROME_COLORS = ["#0a0a0a", "#3f3f3f", "#737373", "#a3a3a3", "#d4d4d4"];

  const StatCard = ({ icon: Icon, label, value, sublabel, accent, testId, onClick }) => (
    <div
      onClick={onClick}
      data-testid={testId}
      className={`p-6 rounded-2xl border border-[hsl(var(--border))] shadow-premium-sm ${
        accent ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]" : "bg-[hsl(var(--surface-elevated))]"
      } ${onClick ? "cursor-pointer hover:shadow-premium premium-hover" : ""}`}
    >
      <div className="flex items-center justify-between mb-4">
        <Icon size={22} weight="duotone" className={accent ? "opacity-80" : "text-[hsl(var(--text-soft))]"} />
        {onClick && <ArrowRight size={16} className={accent ? "opacity-50" : "text-[hsl(var(--text-faint))]"} />}
      </div>
      <p className={`text-[10px] font-semibold uppercase tracking-[0.18em] mb-2 ${accent ? "opacity-70" : "text-[hsl(var(--text-soft))]"}`}>{label}</p>
      <p className={`text-3xl font-display font-bold tracking-tight tabular-nums ${accent ? "" : "text-[hsl(var(--text-strong))]"}`}>{value}</p>
      {sublabel && <p className={`text-xs mt-1 ${accent ? "opacity-60" : "text-[hsl(var(--text-faint))]"}`}>{sublabel}</p>}
    </div>
  );

  const monthlyData = stats.monthly_data || [];

  return (
    <div className="p-8 space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tighter text-[hsl(var(--text-strong))] mb-2">Dashboard</h1>
          <p className="text-sm text-[hsl(var(--text-soft))] font-light">
            {isPersonel
              ? `Atanan müşterileriniz · ${stats.total_customers} müşteri`
              : "Finansal genel bakış · Gelirler ve Sabit Giderler"}
          </p>
        </div>

        <div className="flex items-center gap-2 p-1 rounded-xl bg-[hsl(var(--secondary))] border border-[hsl(var(--border))]" data-testid="dashboard-filter-toggle">
          {[
            { id: "all", label: "Tümü" },
            { id: "month", label: "Ay" },
            { id: "range", label: "Aralık" },
          ].map((opt) => (
            <button
              key={opt.id}
              onClick={() => setFilterMode(opt.id)}
              data-testid={`filter-mode-${opt.id}`}
              className={`px-4 py-1.5 rounded-lg text-xs font-medium ${
                filterMode === opt.id
                  ? "bg-[hsl(var(--surface-elevated))] text-[hsl(var(--text-strong))] shadow-premium-sm"
                  : "text-[hsl(var(--text-soft))] hover:text-[hsl(var(--text-strong))]"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {filterMode === "month" && (
        <div className="flex items-center gap-3 p-4 rounded-xl bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))]">
          <Calendar size={18} weight="duotone" className="text-[hsl(var(--text-soft))]" />
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="dashboard-month-input" className="px-3 py-2 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-sm text-[hsl(var(--text-strong))]" />
          {month && <button onClick={() => setMonth("")} className="text-xs text-[hsl(var(--text-soft))] underline">Temizle</button>}
        </div>
      )}
      {filterMode === "range" && (
        <div className="flex items-center gap-3 p-4 rounded-xl bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))]">
          <Calendar size={18} weight="duotone" className="text-[hsl(var(--text-soft))]" />
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="dashboard-start-date" className="px-3 py-2 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-sm text-[hsl(var(--text-strong))]" />
          <span className="text-sm text-[hsl(var(--text-faint))]">—</span>
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="dashboard-end-date" className="px-3 py-2 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-sm text-[hsl(var(--text-strong))]" />
        </div>
      )}

      {/* OVERDUE ALERT */}
      {overdue && overdue.count > 0 && (
        <div className="rounded-2xl border-2 border-[hsl(var(--danger))]/40 bg-[hsl(var(--danger))]/5 overflow-hidden" data-testid="overdue-alert">
          <div className="p-5 flex items-center gap-4 border-b border-[hsl(var(--danger))]/20">
            <div className="p-3 rounded-xl bg-[hsl(var(--danger))]/10">
              <WarningCircle size={24} weight="duotone" className="text-[hsl(var(--danger))]" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-display font-bold text-[hsl(var(--danger))] tracking-tight">Vadesi Geçen Ödemeler</h3>
              <p className="text-sm text-[hsl(var(--text-soft))]">
                <span className="font-semibold text-[hsl(var(--danger))]">{overdue.count} adet</span> · Toplam <span className="font-bold text-[hsl(var(--danger))] tabular-nums">{fmt(overdue.total_overdue_amount)}</span>
              </p>
            </div>
          </div>
          <div className="max-h-64 overflow-y-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-[hsl(var(--danger))]/5 border-b border-[hsl(var(--danger))]/10">
                  <th className="text-left px-5 py-2.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[hsl(var(--text-soft))]">Müşteri</th>
                  <th className="text-left px-5 py-2.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[hsl(var(--text-soft))]">Vade Tarihi</th>
                  <th className="text-center px-5 py-2.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[hsl(var(--text-soft))]">Gecikme</th>
                  <th className="text-right px-5 py-2.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[hsl(var(--text-soft))]">Tutar</th>
                </tr>
              </thead>
              <tbody>
                {overdue.items.slice(0, 10).map((it, idx) => (
                  <tr key={it.id} className="border-b border-[hsl(var(--danger))]/10" data-testid={`overdue-row-${idx}`}>
                    <td className="px-5 py-3 text-sm font-medium text-[hsl(var(--text-strong))]">{it.customer_name || "-"}</td>
                    <td className="px-5 py-3 text-sm text-[hsl(var(--text-soft))] tabular-nums">{new Date(it.date).toLocaleDateString("tr-TR")}</td>
                    <td className="px-5 py-3 text-center">
                      <span className="inline-block px-2 py-0.5 rounded-md text-[10px] font-bold tabular-nums bg-[hsl(var(--danger))]/15 text-[hsl(var(--danger))]">
                        {it.days_overdue} gün
                      </span>
                    </td>
                    <td className="px-5 py-3 text-sm text-right font-semibold text-[hsl(var(--danger))] tabular-nums">{fmt(it.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {overdue.items.length > 10 && (
              <div className="p-3 text-center text-xs text-[hsl(var(--text-soft))] bg-[hsl(var(--danger))]/5">
                ve {overdue.items.length - 10} adet daha... <button onClick={() => navigate("/income")} className="underline font-medium ml-1">Gelirler sayfasında görüntüle</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Top stats: 4 cards focused on income + fixed expense projection */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={CurrencyCircleDollar} label="Toplam Gelir" value={fmt(stats.total_income)} sublabel="Tahsil edilen" testId="stat-total-income" onClick={() => navigate("/income")} />
        <StatCard icon={Clock} label="Bekleyen Tahsilat" value={fmt(stats.pending_payments)} sublabel={`${overdue?.count || 0} vadesi geçmiş`} testId="stat-pending" onClick={() => navigate("/income")} />
        {isPersonel ? (
          <>
            <StatCard icon={Users} label="Aktif Müşteri" value={stats.total_customers} sublabel="Atanan müşteri sayısı" testId="stat-assigned-customers" onClick={() => navigate("/customers")} />
            <StatCard icon={TrendUp} label="Tahsil Edilen" value={fmt(stats.total_income)} sublabel="Müşterilerinizden gelen" accent testId="stat-personel-revenue" />
          </>
        ) : (
          <>
            <StatCard icon={Receipt} label="Aylık Sabit Gider" value={fmt(stats.fixed_monthly_total)} sublabel={`Yıllık: ${fmt(stats.fixed_yearly_total)}`} testId="stat-fixed-monthly" onClick={() => navigate("/fixed-expenses")} />
            <StatCard icon={stats.net_balance >= 0 ? TrendUp : TrendDown} label="Net Bakiye" value={fmt(stats.net_balance)} sublabel={stats.net_balance >= 0 ? "Tahmini kâr" : "Tahmini zarar"} accent testId="stat-net-balance" />
          </>
        )}
      </div>

      {/* Customer stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard icon={Users} label="Toplam Müşteri" value={stats.total_customers} testId="stat-total-customers" onClick={() => navigate("/customers")} />
        <StatCard icon={Users} label="Yeni Müşteriler" value={stats.new_customers} sublabel="1 işlem ve altı" testId="stat-new-customers" />
        <StatCard icon={Users} label="Daimi Müşteriler" value={stats.permanent_customers} sublabel="2+ işlem" testId="stat-permanent-customers" />
      </div>

      {/* Aylık Gelir Grafiği + Sabit Gider çizgisi */}
      <div className="rounded-2xl bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] p-6 shadow-premium-sm" data-testid="chart-monthly-area">
        <div className="mb-6">
          <h3 className="text-2xl font-display font-bold text-[hsl(var(--text-strong))] tracking-tight">Aylık Gelir Trendi</h3>
          <p className="text-xs text-[hsl(var(--text-soft))] mt-1">Aylık tahsilat ve aya özel sabit gider</p>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={monthlyData}>
            <defs>
              <linearGradient id="gelirGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="hsl(var(--text-strong))" stopOpacity={0.35} />
                <stop offset="100%" stopColor="hsl(var(--text-strong))" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis dataKey="month" stroke="hsl(var(--text-soft))" style={{ fontSize: "11px" }} />
            <YAxis stroke="hsl(var(--text-soft))" style={{ fontSize: "11px" }} tickFormatter={(v) => `₺${(v / 1000).toFixed(0)}K`} />
            <Tooltip contentStyle={{ backgroundColor: "hsl(var(--surface-elevated))", border: "1px solid hsl(var(--border))", borderRadius: "12px", fontSize: "12px" }} formatter={(v) => fmt(v)} />
            <Legend wrapperStyle={{ fontSize: "12px" }} />
            <Area type="monotone" dataKey="gelir" stroke="hsl(var(--text-strong))" strokeWidth={2} fill="url(#gelirGrad)" name="Gelir" />
            {!isPersonel && (
              <Line type="monotone" dataKey="sabit_gider" stroke="hsl(var(--danger))" strokeWidth={2} strokeDasharray="6 4" dot={{ r: 3 }} name="Sabit Gider" />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Two column: Bar net + Pie category */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-2xl bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] p-6 shadow-premium-sm" data-testid="chart-monthly-bar">
          <h3 className="text-xl font-display font-bold text-[hsl(var(--text-strong))] mb-1 tracking-tight">Aylık Net Bakiye</h3>
          <p className="text-xs text-[hsl(var(--text-soft))] mb-4">Gelir − Sabit Gider</p>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={monthlyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="month" stroke="hsl(var(--text-soft))" style={{ fontSize: "11px" }} />
              <YAxis stroke="hsl(var(--text-soft))" style={{ fontSize: "11px" }} tickFormatter={(v) => `₺${(v / 1000).toFixed(0)}K`} />
              <Tooltip contentStyle={{ backgroundColor: "hsl(var(--surface-elevated))", border: "1px solid hsl(var(--border))", borderRadius: "12px", fontSize: "12px" }} formatter={(v) => fmt(v)} />
              <ReferenceLine y={0} stroke="hsl(var(--text-soft))" />
              <Bar dataKey="net" fill="hsl(var(--text-strong))" name="Net" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-2xl bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] p-6 shadow-premium-sm" data-testid="chart-category">
          <h3 className="text-xl font-display font-bold text-[hsl(var(--text-strong))] mb-1 tracking-tight">Gelir Kategorileri</h3>
          <p className="text-xs text-[hsl(var(--text-soft))] mb-4">Hizmet türüne göre tahsilat dağılımı</p>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={stats.category_breakdown} dataKey="amount" nameKey="category" cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={2}>
                {(stats.category_breakdown || []).map((entry, index) => (
                  <Cell key={index} fill={MONOCHROME_COLORS[index % MONOCHROME_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={{ backgroundColor: "hsl(var(--surface-elevated))", border: "1px solid hsl(var(--border))", borderRadius: "12px", fontSize: "12px" }} formatter={(v) => fmt(v)} />
              <Legend wrapperStyle={{ fontSize: "11px" }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Monthly breakdown - card grid (sadeleştirilmiş) */}
      <div data-testid="monthly-cards">
        <div className="flex items-end justify-between mb-4">
          <div>
            <h3 className="text-2xl font-display font-bold text-[hsl(var(--text-strong))] tracking-tight">Aylık Özet</h3>
            <p className="text-xs text-[hsl(var(--text-soft))] mt-1">Her ay için gelir ve bekleyen tahsilat özeti</p>
          </div>
        </div>
        {monthlyData.length === 0 ? (
          <div className="rounded-2xl bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] p-12 text-center text-sm text-[hsl(var(--text-faint))]">Veri bulunamadı</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {monthlyData.map((m, idx) => {
              const toplam = (m.gelir || 0) + (m.bekleyen || 0);
              const isCurrent = idx === monthlyData.length - 1;
              return (
                <div
                  key={m.month_key}
                  data-testid={`monthly-card-${idx}`}
                  className={`p-5 rounded-2xl border shadow-premium-sm ${
                    isCurrent
                      ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] border-[hsl(var(--primary))]"
                      : "bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]"
                  }`}
                >
                  <div className={`flex items-center justify-between mb-3 ${isCurrent ? "" : ""}`}>
                    <p className={`text-[10px] font-bold uppercase tracking-[0.18em] ${isCurrent ? "opacity-80" : "text-[hsl(var(--text-soft))]"}`}>{m.month}</p>
                    {isCurrent && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-white/15 backdrop-blur-sm">Güncel</span>}
                  </div>
                  <div className="space-y-2.5">
                    <div>
                      <p className={`text-[9px] uppercase tracking-wider mb-0.5 ${isCurrent ? "opacity-60" : "text-[hsl(var(--text-faint))]"}`}>Gelir</p>
                      <p className={`text-lg font-display font-bold tabular-nums ${isCurrent ? "" : "text-[hsl(var(--success))]"}`}>{fmt(m.gelir)}</p>
                    </div>
                    {m.bekleyen > 0 && (
                      <div>
                        <p className={`text-[9px] uppercase tracking-wider mb-0.5 ${isCurrent ? "opacity-60" : "text-[hsl(var(--text-faint))]"}`}>Bekleyen</p>
                        <p className={`text-sm font-semibold tabular-nums ${isCurrent ? "opacity-90" : "text-[hsl(var(--warning))]"}`}>{fmt(m.bekleyen)}</p>
                      </div>
                    )}
                    <div className={`pt-2 border-t ${isCurrent ? "border-white/15" : "border-[hsl(var(--border))]"}`}>
                      <p className={`text-[9px] uppercase tracking-wider mb-0.5 ${isCurrent ? "opacity-60" : "text-[hsl(var(--text-faint))]"}`}>Toplam</p>
                      <p className={`text-base font-bold tabular-nums ${isCurrent ? "" : "text-[hsl(var(--text-strong))]"}`}>{fmt(toplam)}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Sektör Dağılımı + Top Müşteriler */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-2xl bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] p-6 shadow-premium-sm" data-testid="chart-sector">
          <h3 className="text-xl font-display font-bold text-[hsl(var(--text-strong))] mb-1 tracking-tight">Sektör Dağılımı</h3>
          <p className="text-xs text-[hsl(var(--text-soft))] mb-4">Sektörlere göre tahsilat ve müşteri sayısı</p>
          {(stats.sector_breakdown || []).filter(s => s.revenue > 0 || s.customer_count > 0).length === 0 ? (
            <div className="text-center py-10 text-sm text-[hsl(var(--text-faint))]">Henüz sektör atanmış müşteri yok</div>
          ) : (
            <div className="space-y-3">
              {(stats.sector_breakdown || []).map((s, idx) => {
                const maxRev = Math.max(...(stats.sector_breakdown || []).map(x => x.revenue), 1);
                const pct = (s.revenue / maxRev) * 100;
                return (
                  <div key={idx} data-testid={`sector-item-${idx}`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-sm font-medium text-[hsl(var(--text-strong))]">{s.sector}</span>
                      <span className="text-xs text-[hsl(var(--text-soft))] tabular-nums">{s.customer_count} müşteri · {fmt(s.revenue)}</span>
                    </div>
                    <div className="h-2 rounded-full bg-[hsl(var(--secondary))] overflow-hidden">
                      <div className="h-full bg-[hsl(var(--text-strong))]" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="rounded-2xl bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] p-6 shadow-premium-sm" data-testid="top-customers">
          <h3 className="text-xl font-display font-bold text-[hsl(var(--text-strong))] mb-1 tracking-tight">En Çok Ciro Getiren Müşteriler</h3>
          <p className="text-xs text-[hsl(var(--text-soft))] mb-4">İlk 10 müşteri (tahsil edilen)</p>
          {(stats.top_customers || []).length === 0 ? (
            <div className="text-center py-10 text-sm text-[hsl(var(--text-faint))]">Henüz veri yok</div>
          ) : (
            <div className="space-y-2">
              {(stats.top_customers || []).map((c, idx) => (
                <div key={c.id} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-[hsl(var(--secondary))]/50" data-testid={`top-customer-${idx}`}>
                  <span className="w-6 h-6 rounded-full bg-[hsl(var(--secondary))] flex items-center justify-center text-[10px] font-bold text-[hsl(var(--text-soft))] tabular-nums flex-shrink-0">{idx + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[hsl(var(--text-strong))] truncate">{c.name}</p>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      {c.customer_type && <span className="text-[9px] uppercase text-[hsl(var(--text-faint))]">{c.customer_type}</span>}
                      {c.sector && <span className="text-[9px] uppercase text-[hsl(var(--accent-gold))]">· {c.sector === "e-ticaret" ? "E-Ticaret" : c.sector === "hizmet" ? "Hizmet" : c.sector}</span>}
                    </div>
                  </div>
                  <p className="text-sm font-bold tabular-nums text-[hsl(var(--text-strong))] flex-shrink-0">{fmt(c.revenue)}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Recent income transactions */}
      <div className="rounded-2xl bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] overflow-hidden shadow-premium-sm" data-testid="recent-transactions">
        <div className="p-6 border-b border-[hsl(var(--border))]">
          <h3 className="text-xl font-display font-bold text-[hsl(var(--text-strong))] tracking-tight">Son Gelir İşlemleri</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-[hsl(var(--secondary))] border-b border-[hsl(var(--border))]">
                <th className="text-left px-5 py-3 text-[10px] font-bold uppercase tracking-[0.15em] text-[hsl(var(--text-soft))]">Tarih</th>
                <th className="text-left px-5 py-3 text-[10px] font-bold uppercase tracking-[0.15em] text-[hsl(var(--text-soft))]">Müşteri</th>
                <th className="text-left px-5 py-3 text-[10px] font-bold uppercase tracking-[0.15em] text-[hsl(var(--text-soft))]">Kategori</th>
                <th className="text-right px-5 py-3 text-[10px] font-bold uppercase tracking-[0.15em] text-[hsl(var(--text-soft))]">Tutar</th>
                <th className="text-center px-5 py-3 text-[10px] font-bold uppercase tracking-[0.15em] text-[hsl(var(--text-soft))]">Durum</th>
              </tr>
            </thead>
            <tbody>
              {(stats.recent_transactions || []).map((t, idx) => (
                <tr key={t.id} className="border-b border-[hsl(var(--border))] hover:bg-[hsl(var(--secondary))]/40" data-testid={`recent-transaction-${idx}`}>
                  <td className="px-5 py-3 text-sm text-[hsl(var(--text-strong))] tabular-nums">{new Date(t.date).toLocaleDateString("tr-TR")}</td>
                  <td className="px-5 py-3 text-sm font-medium text-[hsl(var(--text-strong))]">{t.customer_name || "-"}</td>
                  <td className="px-5 py-3 text-sm text-[hsl(var(--text-soft))]">{t.category}</td>
                  <td className="px-5 py-3 text-sm text-right tabular-nums font-semibold text-[hsl(var(--text-strong))]">{fmt(t.amount)}</td>
                  <td className="px-5 py-3 text-center">
                    <span className={`text-[10px] font-medium ${t.payment_status === "ödendi" ? "text-[hsl(var(--success))]" : "text-[hsl(var(--warning))]"}`}>{t.payment_status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
